/**
 * StudioStatsService.ts
 * Agrégats RÉELS depuis Supabase pour les vues Live et Kiosque.
 *
 * Aucune donnée mockée : toutes les valeurs proviennent des tables
 * `template_editor_projects`, `template_editor_project_exports`,
 * `template_editor_templates`. Les compteurs sont scoppés à l'utilisateur
 * courant via les politiques RLS.
 */
import { supabase, isSupabaseConfigured } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';

export interface RecentExport {
  id: string;
  filename: string;
  format: string;
  exportedAt: string;
  sizeBytes: number | null;
}

export interface RecentProject {
  id: string;
  name: string;
  updatedAt: string;
}

export interface StudioStats {
  projectsCount: number;
  templatesCount: number;
  exportsCount: number;
  totalExportBytes: number;
  recentExports: RecentExport[];
  recentProjects: RecentProject[];
  /** Nom du projet le plus récemment modifié (titre d'événement réel). */
  latestProjectName: string | null;
  latestProjectUpdatedAt: string | null;
}

const EMPTY_STATS: StudioStats = {
  projectsCount: 0,
  templatesCount: 0,
  exportsCount: 0,
  totalExportBytes: 0,
  recentExports: [],
  recentProjects: [],
  latestProjectName: null,
  latestProjectUpdatedAt: null,
};

export class StudioStatsService {
  /**
   * Charge tous les agrégats réels en parallèle.
   * Retourne des zéros (pas d'erreur) si Supabase n'est pas configuré.
   */
  async getStudioStats(): Promise<StudioStats> {
    if (!isSupabaseConfigured() || !supabase) {
      logger.warn('StudioStatsService : Supabase non configuré — stats vides');
      return { ...EMPTY_STATS };
    }

    const client = supabase;

    try {
      const [projectsCountRes, templatesCountRes, exportsCountRes, exportsRes, projectsRes] =
        await Promise.all([
          client.from('template_editor_projects').select('id', { count: 'exact', head: true }),
          client.from('template_editor_templates').select('id', { count: 'exact', head: true }),
          client
            .from('template_editor_project_exports')
            .select('id', { count: 'exact', head: true }),
          client
            .from('template_editor_project_exports')
            .select('id, filename, format, exported_at, size_bytes')
            .order('exported_at', { ascending: false })
            .limit(12),
          client
            .from('template_editor_projects')
            .select('id, name, updated_at')
            .order('updated_at', { ascending: false })
            .limit(8),
        ]);

      const recentExports: RecentExport[] = (exportsRes.data ?? []).map(e => ({
        id: e.id,
        filename: e.filename,
        format: e.format,
        exportedAt: e.exported_at,
        sizeBytes: e.size_bytes,
      }));

      const totalExportBytes = recentExports.reduce((sum, e) => sum + (e.sizeBytes ?? 0), 0);

      const recentProjects: RecentProject[] = (projectsRes.data ?? []).map(p => ({
        id: p.id,
        name: p.name,
        updatedAt: p.updated_at,
      }));

      return {
        projectsCount: projectsCountRes.count ?? 0,
        templatesCount: templatesCountRes.count ?? 0,
        exportsCount: exportsCountRes.count ?? 0,
        totalExportBytes,
        recentExports,
        recentProjects,
        latestProjectName: recentProjects[0]?.name ?? null,
        latestProjectUpdatedAt: recentProjects[0]?.updatedAt ?? null,
      };
    } catch (error) {
      logger.error('StudioStatsService : échec du chargement des stats', { error });
      return { ...EMPTY_STATS };
    }
  }

  /**
   * Enregistre un export réel dans `template_editor_project_exports`.
   * Nécessite un projet sauvegardé (FK project_id). No-op si non connecté
   * ou si le projet n'a pas encore d'id. Alimente les compteurs Live/Kiosque.
   */
  async recordExport(input: {
    projectId: string | null;
    format: string;
    filename: string;
    sizeBytes?: number | null;
    dpi?: number | null;
    width?: number | null;
    height?: number | null;
    pixelRatio?: number | null;
  }): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) return;
    if (!input.projectId) {
      logger.info('recordExport ignoré : projet non sauvegardé (pas de project_id)');
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.from('template_editor_project_exports').insert({
        user_id: user.id,
        project_id: input.projectId,
        format: input.format,
        filename: input.filename,
        size_bytes: input.sizeBytes ?? null,
        dpi: input.dpi ?? null,
        width: input.width ?? null,
        height: input.height ?? null,
        pixel_ratio: input.pixelRatio ?? null,
      });
      if (error) {
        logger.warn('recordExport : échec insertion export', { error: error.message });
      }
    } catch (error) {
      logger.warn('recordExport : exception', { error });
    }
  }

  /**
   * S'abonne aux nouveaux exports en temps réel (Supabase Realtime).
   * `onInsert` est appelé à chaque nouvel export inséré.
   * Retourne une fonction de désabonnement.
   */
  subscribeToExports(onInsert: (exp: RecentExport) => void): () => void {
    if (!isSupabaseConfigured() || !supabase) {
      return () => {};
    }

    const channel = supabase
      .channel('studio-exports-live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'template_editor_project_exports' },
        payload => {
          const row = payload.new as {
            id: string;
            filename: string;
            format: string;
            exported_at: string;
            size_bytes: number | null;
          };
          onInsert({
            id: row.id,
            filename: row.filename,
            format: row.format,
            exportedAt: row.exported_at,
            sizeBytes: row.size_bytes,
          });
        }
      )
      .subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }
}

export const studioStatsService = new StudioStatsService();

/** Formate un nombre d'octets en Ko / Mo / Go lisibles (fr-FR). */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '0 o';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} ${units[i]}`;
}

/** Temps relatif court en français (« il y a 3 min », « à l'instant »). */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 10) return "à l'instant";
  if (diffSec < 60) return `il y a ${diffSec} s`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.round(h / 24);
  return `il y a ${d} j`;
}
